import {
  CalendarOutlineIcon,
  ChildrenSolidIcon,
  ErrorOutlineIcon,
  HelpPathOutlineIcon,
  InfoOutlineIcon,
  ProgressActivityOutlineIcon,
} from '@ikyucom/design-system-icons-react'
import { CheckBox } from 'macaron-frontend-ui'
import { useCallback } from 'react'
import { format as formatWithDate } from '~/lib/date/date'
import { format as formatWithDateTime } from '~/lib/date/dateTime'
import { useIsPC } from '~/lib/ua'
import { getHelpRequestUrl } from '~/lib/url'
import { useReserveNavigate } from '~/navigation'
import { css, cva } from '~/styled-system/css'
import { vstack } from '~/styled-system/patterns'
import { useCancelFeeStartDateTime, useIsSubjectToCancelFee } from '../../store/cancelPolicy'
import { useTokenErrorOnReserve } from '../../store/card'
import { useAdultCount } from '../../store/childMenu'
import {
  useAuthenticatorProps,
  useIsReservable,
  useReserveButtonLabel,
  useSubmit,
} from '../../store/reserve'
import {
  useAgreedToReserveRequest,
  useChildCount,
  useIsReserving,
  useRestaurantId,
  useVisitDate,
  useVisitTime,
} from '../../store/stateMachine/context'
import { useIsMemberOrPreMember, usePlan } from '../../store/stateMachine/state'
import {
  useDisplayAuthMessage,
  useErrorMessages,
  useMarkUserInteracted,
  useSetAgreedToReserveRequest,
  useShouldShowErrors,
} from '../../store/validate'
import type { AuthenticatorResult } from './Authenticator'
import { Authenticator } from './Authenticator'

export function ReservationConfirmation() {
  return (
    <div
      className={css({
        spaceY: '5',
        bg: 'white',
        paddingX: '3',
        paddingBottom: '5',
        paddingTop: '3',
        _pc: {
          padding: '6',
        },
      })}
    >
      <ErrorMessages />
      <div className={css({ spaceY: '3', _pc: { w: '[480px]', marginX: 'auto' } })}>
        <Summary />
        <RequestReservation />
        <ConfirmationList />
        <ReserveButton />
        <AuthMessage />
        <AuthModal />
      </div>
    </div>
  )
}

function Summary() {
  const { visitDate, visitTime, adultCount, childCount } = useSummary()
  if (visitDate === undefined || visitTime === undefined) {
    return null
  }
  return (
    <div className="flex items-center justify-center space-x-3 text-md font-bold">
      <div className="flex items-center space-x-1">
        <CalendarOutlineIcon width={14} height={14} className={css({ color: 'primary' })} />
        <p>
          {formatWithDate(visitDate, 'M月d日(E)')}
          {visitTime}
        </p>
      </div>
      <div className="flex items-center space-x-1">
        <ChildrenSolidIcon width={14} height={14} className={css({ color: 'primary' })} />
        <span>大人{adultCount}名</span>
        {childCount > 0 && <span>子供{childCount}名</span>}
      </div>
    </div>
  )
}

function RequestReservation() {
  const { isRequestReservation, checked, onChange } = useRequestReservation()
  const { openNotes } = useConfirmationList()
  if (isRequestReservation === undefined || !isRequestReservation) {
    return null
  }
  return (
    <div className="space-y-3 border border-neutral-300 bg-neutral-000 p-4 text-center">
      <div>
        <div className="flex items-center justify-center space-x-1 text-lg">
          <p className="font-bold">このご予約はリクエスト予約です</p>
          <a href={getHelpRequestUrl()} target="_blank" rel="noreferrer">
            <HelpPathOutlineIcon className="text-neutral-600" />
          </a>
        </div>
        <p className="text-sm font-bold">
          必ず
          <button type="button" className={css({ color: 'link' })} onClick={openNotes}>
            注意事項
          </button>
          をご確認ください。
        </p>
      </div>
      <div className={css({ spaceY: '1' })}>
        <div
          className={css({
            spaceY: '2',
            borderRadius: 'default',
            bg: 'gray.100',
            p: '2',
            textAlign: 'left',
          })}
        >
          <p className={css({ fontSize: 'base', color: 'error.text' })}>
            お申し込み時点では予約は完了していません。
          </p>
          <p className={css({ fontSize: 'sm' })}>
            空席状況を確認後、72時間以内に予約が完了いたします。予約成立・不成立は、予約詳細にてご確認ください。
          </p>
        </div>
        <p className="flex text-left text-xs text-neutral-700">
          ※店舗からご連絡をする場合がございます。必ずご連絡のつく電話番号・メールアドレスを入力してください。
        </p>
      </div>
      <div className="inline-flex items-center justify-center">
        <CheckBox label="上記事項を確認しました。" checked={checked} onChange={onChange} />
      </div>
    </div>
  )
}

function useRequestReservation() {
  const isRequestReservation = usePlan()?.isRequestReservation
  const checked = useAgreedToReserveRequest()
  const change = useSetAgreedToReserveRequest()
  const onChange = useCallback(() => {
    change(!checked)
  }, [change, checked])
  return { isRequestReservation, checked, onChange }
}

const ConfirmationList =
  import.meta.env.VITE_MODE === 'ppg' ? PpgConfirmationList : IkyuConfirmationList

function PpgConfirmationList() {
  const { openCancelPolicy, openNotes } = useConfirmationList()
  const restaurantId = useRestaurantId()
  return (
    <div className={vstack({ gap: '2' })}>
      <div className="text-xs text-neutral-700">
        一休の
        <a
          href="https://restaurant.ikyu.com/rsMirror/rsMirrorTerms.asp"
          target="_blank"
          rel="noreferrer"
          className={css({ color: 'link' })}
        >
          利用規約
        </a>
        およびお店の
        <button type="button" className={css({ color: 'link' })} onClick={openNotes}>
          注意事項
        </button>
        、
        <button type="button" className={css({ color: 'link' })} onClick={openCancelPolicy}>
          キャンセルポリシー
        </button>
        に同意し、予約します。 特定商取引法に基づく表示は
        <a
          href={`/${restaurantId}/policies`}
          target="_blank"
          rel="noreferrer"
          className={css({ color: 'link' })}
        >
          こちら
        </a>
        をご確認ください。
      </div>
      <ConsentDetails />
    </div>
  )
}

function IkyuConfirmationList() {
  const { openCancelPolicy, openNotes, isMemberOrPreMember } = useConfirmationList()
  return (
    <div className={vstack({ gap: '2' })}>
      <div className="text-xs text-neutral-700">
        株式会社一休は、お客様の個人情報、個人関連情報等を個人情報保護方針に従って取り扱います。
        <a
          href="https://www.ikyu.co.jp/policy/"
          target="_blank"
          rel="noreferrer"
          className={css({ color: 'link' })}
        >
          個人情報保護方針
        </a>
        、
        {isMemberOrPreMember && (
          <>
            <a
              href="https://www.ikyu.com/help/ex/kiyaku.htm"
              target="_blank"
              rel="noreferrer"
              className={css({ color: 'link' })}
            >
              会員規約
            </a>
            、
          </>
        )}
        <a
          href="https://restaurant.ikyu.com/rsMirror/rsMirrorTerms.asp"
          target="_blank"
          rel="noreferrer"
          className={css({ color: 'link' })}
        >
          利用規約
        </a>
        、
        <button type="button" className={css({ color: 'link' })} onClick={openNotes}>
          注意事項
        </button>
        および
        <button type="button" className={css({ color: 'link' })} onClick={openCancelPolicy}>
          キャンセルポリシー
        </button>
        に同意し、予約します。
      </div>
    </div>
  )
}

function useConfirmationList() {
  const navigate = useReserveNavigate()
  const openCancelPolicy = useCallback(() => {
    navigate({ type: 'showCancelPolicy' })
  }, [navigate])
  const isMemberOrPreMember = useIsMemberOrPreMember()
  const openNotes = useCallback(() => {
    navigate({ type: 'showNote' })
  }, [navigate])
  return { openCancelPolicy, openNotes, isMemberOrPreMember }
}

// PPGの同意文言
function ConsentDetails() {
  return (
    <ul className={css({ fontSize: 'xs', color: 'gray.700' })}>
      <li>
        ※入力した情報はLINEヤフー株式会社（以下、LINEヤフー）と一休が取得します。LINEヤフーおよび一休は予約に関してお客様が閲覧した広告情報等の個人関連情報を第三者から取得する場合があります。
      </li>
      <li>
        ※LINEヤフーと一休が連携してサービスを提供するため、LINEヤフーはお客様の識別情報等を一休に、一休は予約内容の識別情報等をLINEヤフーに、それぞれ提供します。
      </li>
      <li>
        ※LINEヤフーは取得した情報を
        <a
          href="https://www.lycorp.co.jp/ja/company/privacypolicy/"
          target="_blank"
          rel="noreferrer"
          className={css({ color: 'link' })}
        >
          プライバシーポリシー
        </a>
        に従って、一休は
        <a
          href="https://www.ikyu.co.jp/policy/"
          target="_blank"
          rel="noreferrer"
          className={css({ color: 'link' })}
        >
          個人情報保護方針
        </a>
        に従って取り扱います。
      </li>
      <li>
        ※LINEヤフーが取得した情報は、本ウェブサイトの運営を委託している一休からAmazon Web Services,
        Inc.（米国法人。保管先は日本国内のサーバー）および、Twillio
        Inc.（米国法人。保管先は米国内のサーバー）に再委託されます。
      </li>
    </ul>
  )
}

function ErrorMessages() {
  const errors = useErrorMessages()
  const shouldShow = useShouldShowErrors()
  const cardTokenError = useTokenErrorOnReserve()
  if (
    (!shouldShow || errors.length === 0) &&
    (cardTokenError === undefined || cardTokenError === '')
  ) {
    return null
  }
  return (
    <div
      className={css({
        spaceY: '1',
        borderWidth: 'thin',
        borderColor: 'error.border',
        paddingX: '4',
        paddingY: '3',
        fontSize: 'sm',
        _pc: { w: '[480px]', marginX: 'auto' },
      })}
    >
      <div className="flex items-center justify-center space-x-1 text-base">
        <ErrorOutlineIcon className={css({ color: 'error.text' })} />
        <span className="font-bold">入力エラーがあります</span>
      </div>
      {errors.map((error) => {
        return (
          <p
            key={error}
            className={css({ display: 'flex', color: 'error.text', _before: { content: '"・"' } })}
          >
            {error}
          </p>
        )
      })}
      {cardTokenError && (
        <p className={css({ display: 'flex', color: 'error.text', _before: { content: '"・"' } })}>
          {cardTokenError}
        </p>
      )}
    </div>
  )
}

const reserveButtonStyle = cva({
  base: {
    w: 'full',
    spaceY: '1',
    borderRadius: 'md',
    p: '3',
    color: 'white',
    _pc: {
      w: 'auto',
      marginX: 'auto',
      paddingY: '2',
      paddingX: '6',
      spaceY: '0',
    },
  },
  variants: {
    state: {
      disabled: {
        cursor: 'default',
        bg: 'gray.400',
      },
      isReserving: {
        cursor: 'default',
        bgGradient: 'button.primary',
      },
      isReservable: {
        cursor: 'pointer',
        bgGradient: 'button.primary',
        _hover: { opacity: '75' },
      },
    },
  },
})

function ReserveButton() {
  const { ...props } = useReserveButton()
  const isReserving = useIsReserving()
  const isPC = useIsPC()
  return (
    <div className={css({ _pc: { textAlign: 'center' } })}>
      <button
        type="button"
        className={reserveButtonStyle({ state: props.state })}
        style={isPC ? { minWidth: '340px', minHeight: '48px' } : {}}
        onClick={props.onClick}
      >
        {isReserving ? (
          <Loading />
        ) : (
          <div
            className={css({
              display: 'flex',
              flexDir: 'column',
              justifyContent: 'center',
              alignItems: 'center',
            })}
            style={{ height: isPC ? '45px' : '32px', width: isPC ? '300px' : '100%' }}
          >
            <span
              className={css({
                fontSize: 'xl',
                fontWeight: 'bold',
                lineHeight: 'none',
                _pc: { lineHeight: 'normal' },
              })}
            >
              {props.label}
            </span>
            <CancelFeeStartDate />
          </div>
        )}
      </button>
    </div>
  )
}

function Loading() {
  const isPC = useIsPC()
  return (
    <div
      className={css({
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      })}
      style={{ height: isPC ? '45px' : '32px', width: isPC ? '300px' : '100%' }}
    >
      <ProgressActivityOutlineIcon
        className={css({
          color: 'white',
          animation: 'loading',
        })}
        style={{ fontSize: '24px' }}
      />
    </div>
  )
}

function useReserveButton(): {
  state: 'isReserving' | 'disabled' | 'isReservable'
  label: string
  onClick: () => void
} {
  const isReservable = useIsReservable()
  const isReserving = useIsReserving()
  const label = useReserveButtonLabel()
  const submit = useSubmit()
  const markInteracted = useMarkUserInteracted()
  const onClick = useCallback(() => {
    submit()
  }, [submit])
  // ../../store/specification.md 「入力エラーの表示について」参照
  const onClickDisabled = useCallback(() => {
    markInteracted()
  }, [markInteracted])

  if (isReserving) {
    return { state: 'isReserving', label, onClick: nop }
  }
  if (!isReservable) {
    return { state: 'disabled', label, onClick: onClickDisabled }
  }
  return { state: 'isReservable', label, onClick }
}
function nop() {}

function AuthMessage() {
  const display = useDisplayAuthMessage()
  if (display.type === 'None') {
    return null
  }
  return (
    <div>
      <div className="flex items-center justify-center text-neutral-700">
        <InfoOutlineIcon className="mr-1" />
        {display.authType === 'PHONE_NUMBER' ? 'SMS' : 'メールアドレス'}
        によるご本人確認後に予約が完了します
      </div>
    </div>
  )
}

function AuthModal() {
  const { isMobileNumber, phoneNumber, email, showAuthModal, close, authenticate } =
    useAuthenticatorProps()

  const onAuth = useCallback(
    (result: AuthenticatorResult) => {
      authenticate(result.key, result.authCode)
    },
    [authenticate]
  )

  const onClose = useCallback(() => {
    close()
  }, [close])

  return (
    <Authenticator
      isMobileNumber={isMobileNumber}
      phoneNumber={phoneNumber}
      email={email}
      opened={showAuthModal}
      onAuth={onAuth}
      onClose={onClose}
    />
  )
}

function useSummary() {
  const visitDate = useVisitDate()
  const visitTime = useVisitTime()
  const adultCount = useAdultCount()
  const childCount = useChildCount()
  return { visitDate, visitTime, adultCount, childCount }
}

function useCancelFeeStartDate() {
  const cancelFeeStartDate = useCancelFeeStartDateTime()
  const isSubjectToCancelFee = useIsSubjectToCancelFee()
  return { cancelFeeStartDate, isSubjectToCancelFee }
}

function CancelFeeStartDate() {
  const { cancelFeeStartDate, isSubjectToCancelFee } = useCancelFeeStartDate()
  if (cancelFeeStartDate === undefined) {
    return null
  }
  return (
    <p
      className={css({
        fontSize: 'sm',
        fontWeight: 'normal',
        lineHeight: 'none',
        color: 'white',
        pt: '1',
        _pc: { lineHeight: 'normal', pt: '0' },
      })}
    >
      {isSubjectToCancelFee
        ? '予約完了後'
        : `${formatWithDateTime(cancelFeeStartDate, 'yyyy年M月d日HH:mm')}`}
      からキャンセル料が発生します
    </p>
  )
}
